import { EMERALD_THEME } from '../../../../config/designTokens';
/**
 * 法律尽职调查组件
 * 步骤3.1: 法律尽职调查
 * 基于 PROFESSIONAL_DUE_DILIGENCE_DESIGN.md 设计规范
 */

import React from 'react';
import {
  Form,
  Row,
  Col,
  Input,
  Select,
  Radio,
  DatePicker,
  InputNumber,
  Button,
} from 'antd';
import { UnifiedDataTable } from '../../../../components/unified/UnifiedDataTable';
import {
  BankOutlined,
  FileTextOutlined,
  ExclamationCircleOutlined,
  CopyrightOutlined,
  AlertOutlined,
  PlusOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  FormSection,
  FormSubSection,
  ConditionalField,
  WhenTrue,
  WhenFalse,
  RiskMatrix,
  DocumentUpload,
  useDDAutoSave,
  useDDValidation,
  DDValidationRules,
  AutoSaveIndicator,
} from './shared';
import type {
  LegalDueDiligenceProps,
  LegalDueDiligenceData,
  EquityChange,
  MajorContract,
  LitigationCase,
  LegalRisk,
  RiskLevel,
} from './shared/types';

const { Option } = Select;
const { TextArea } = Input;

const LegalDueDiligence: React.FC<LegalDueDiligenceProps> = ({
  projectId,
  onStepComplete,
  onStepSave,
  readonly = false,
}) => {
  const [form] = Form.useForm<LegalDueDiligenceData>();

  // 使用专业尽调专用Hooks
  const { validateForm } = useDDValidation({
    form,
    stepType: 'legal',
  });

  const { saving, saveStatus, lastSavedText, triggerAutoSave, manualSave } =
    useDDAutoSave({
      projectId,
      stepType: 'legal',
      enabled: true,
      onSave: async data => {
        console.log('Legal DD auto saved:', data);
      },
    });

  // 监听表单变化
  const handleFormChange = () => {
    const currentValues = form.getFieldsValue();
    triggerAutoSave(currentValues);
  };

  // 保存草稿 - 通过统一操作栏触发
  const handleSaveDraft = async () => {
    const values = form.getFieldsValue();
    await manualSave(values);
    onStepSave?.('legal_due_diligence', values);
  };

  // 提交表单 - 通过统一操作栏触发
  const handleSubmit = async () => {
    const result = await validateForm();
    if (result.success) {
      onStepComplete?.('legal_due_diligence', result.values);
    }
  };

  // 重大合同表格列定义
  const majorContractColumns = [
    {
      title: '合同名称',
      dataIndex: 'contractName',
      key: 'contractName',
      width: 200,
      render: (text: string, record: MajorContract, index: number) => (
        <Input
          value={text}
          onChange={e => {
            const contracts =
              form.getFieldValue(['majorContracts', 'contractList']) || [];
            const newContracts = [...contracts];
            newContracts[index] = {
              ...newContracts[index],
              contractName: e.target.value,
            };
            form.setFieldValue(
              ['majorContracts', 'contractList'],
              newContracts
            );
            handleFormChange();
          }}
          disabled={readonly}
          placeholder="请输入合同名称"
        />
      ),
    },
    {
      title: '合同类型',
      dataIndex: 'contractType',
      key: 'contractType',
      width: 150,
      render: (text: string, record: MajorContract, index: number) => (
        <Select
          value={text}
          onChange={value => {
            const contracts =
              form.getFieldValue(['majorContracts', 'contractList']) || [];
            const newContracts = [...contracts];
            newContracts[index] = {
              ...newContracts[index],
              contractType: value,
            };
            form.setFieldValue(
              ['majorContracts', 'contractList'],
              newContracts
            );
            handleFormChange();
          }}
          disabled={readonly}
          placeholder="选择类型"
          style={{ width: '100%' }}
        >
          <Option value="sales_contract">销售合同</Option>
          <Option value="purchase_contract">采购合同</Option>
          <Option value="service_contract">服务合同</Option>
          <Option value="lease_contract">租赁合同</Option>
          <Option value="loan_contract">借款合同</Option>
          <Option value="guarantee_contract">担保合同</Option>
          <Option value="joint_venture">合资协议</Option>
          <Option value="cooperation_agreement">合作协议</Option>
          <Option value="licensing_agreement">许可协议</Option>
          <Option value="other">其他</Option>
        </Select>
      ),
    },
    {
      title: '合同对方',
      dataIndex: 'counterparty',
      key: 'counterparty',
      width: 200,
      render: (text: string, record: MajorContract, index: number) => (
        <Input
          value={text}
          onChange={e => {
            const contracts =
              form.getFieldValue(['majorContracts', 'contractList']) || [];
            const newContracts = [...contracts];
            newContracts[index] = {
              ...newContracts[index],
              counterparty: e.target.value,
            };
            form.setFieldValue(
              ['majorContracts', 'contractList'],
              newContracts
            );
            handleFormChange();
          }}
          disabled={readonly}
          placeholder="合同对方名称"
        />
      ),
    },
    {
      title: '合同金额(万元)',
      dataIndex: 'contractAmount',
      key: 'contractAmount',
      width: 120,
      render: (value: number, record: MajorContract, index: number) => (
        <InputNumber
          value={value}
          onChange={val => {
            const contracts =
              form.getFieldValue(['majorContracts', 'contractList']) || [];
            const newContracts = [...contracts];
            newContracts[index] = {
              ...newContracts[index],
              contractAmount: val || 0,
            };
            form.setFieldValue(
              ['majorContracts', 'contractList'],
              newContracts
            );
            handleFormChange();
          }}
          disabled={readonly}
          placeholder="金额"
          style={{ width: '100%' }}
          min={0}
          precision={2}
        />
      ),
    },
    {
      title: '签署日期',
      dataIndex: 'signDate',
      key: 'signDate',
      width: 120,
      render: (text: string, record: MajorContract, index: number) => (
        <DatePicker
          value={text ? dayjs(text) : null}
          onChange={date => {
            const contracts =
              form.getFieldValue(['majorContracts', 'contractList']) || [];
            const newContracts = [...contracts];
            newContracts[index] = {
              ...newContracts[index],
              signDate: date ? date.format('YYYY-MM-DD') : '',
            };
            form.setFieldValue(
              ['majorContracts', 'contractList'],
              newContracts
            );
            handleFormChange();
          }}
          disabled={readonly}
          placeholder="签署日期"
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: '生效日期',
      dataIndex: 'effectiveDate',
      key: 'effectiveDate',
      width: 120,
      render: (text: string, record: MajorContract, index: number) => (
        <DatePicker
          value={text ? dayjs(text) : null}
          onChange={date => {
            const contracts =
              form.getFieldValue(['majorContracts', 'contractList']) || [];
            const newContracts = [...contracts];
            newContracts[index] = {
              ...newContracts[index],
              effectiveDate: date ? date.format('YYYY-MM-DD') : '',
            };
            form.setFieldValue(
              ['majorContracts', 'contractList'],
              newContracts
            );
            handleFormChange();
          }}
          disabled={readonly}
          placeholder="生效日期"
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: '到期日期',
      dataIndex: 'expiryDate',
      key: 'expiryDate',
      width: 120,
      render: (text: string, record: MajorContract, index: number) => (
        <DatePicker
          value={text ? dayjs(text) : null}
          onChange={date => {
            const contracts =
              form.getFieldValue(['majorContracts', 'contractList']) || [];
            const newContracts = [...contracts];
            newContracts[index] = {
              ...newContracts[index],
              expiryDate: date ? date.format('YYYY-MM-DD') : '',
            };
            form.setFieldValue(
              ['majorContracts', 'contractList'],
              newContracts
            );
            handleFormChange();
          }}
          disabled={readonly}
          placeholder="到期日期"
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: '关键条款',
      dataIndex: 'keyTerms',
      key: 'keyTerms',
      width: 200,
      render: (text: string, record: MajorContract, index: number) => (
        <TextArea
          value={text}
          onChange={e => {
            const contracts =
              form.getFieldValue(['majorContracts', 'contractList']) || [];
            const newContracts = [...contracts];
            newContracts[index] = {
              ...newContracts[index],
              keyTerms: e.target.value,
            };
            form.setFieldValue(
              ['majorContracts', 'contractList'],
              newContracts
            );
            handleFormChange();
          }}
          disabled={readonly}
          placeholder="关键条款说明"
          autoSize={{ minRows: 2, maxRows: 4 }}
        />
      ),
    },
    {
      title: '风险评估',
      dataIndex: 'riskAssessment',
      key: 'riskAssessment',
      width: 200,
      render: (text: string, record: MajorContract, index: number) => (
        <TextArea
          value={text}
          onChange={e => {
            const contracts =
              form.getFieldValue(['majorContracts', 'contractList']) || [];
            const newContracts = [...contracts];
            newContracts[index] = {
              ...newContracts[index],
              riskAssessment: e.target.value,
            };
            form.setFieldValue(
              ['majorContracts', 'contractList'],
              newContracts
            );
            handleFormChange();
          }}
          disabled={readonly}
          placeholder="风险评估意见"
          autoSize={{ minRows: 2, maxRows: 4 }}
        />
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 80,
      render: (text: any, record: MajorContract, index: number) => (
        <Button
          type="link"
          danger
          size="small"
          icon={<DeleteOutlined />}
          onClick={() => {
            const contracts =
              form.getFieldValue(['majorContracts', 'contractList']) || [];
            const newContracts = [...contracts];
            newContracts.splice(index, 1);
            form.setFieldValue(
              ['majorContracts', 'contractList'],
              newContracts
            );
            handleFormChange();
          }}
          disabled={readonly}
        >
          删除
        </Button>
      ),
    },
  ];

  // 股权变更记录表格列定义
  const equityChangeColumns = [
    {
      title: '变更日期',
      dataIndex: 'changeDate',
      key: 'changeDate',
      width: 120,
      render: (text: string, record: EquityChange, index: number) => (
        <DatePicker
          value={text ? dayjs(text) : null}
          onChange={date => {
            const changes =
              form.getFieldValue(['legalStatus', 'equityChanges']) || [];
            const newChanges = [...changes];
            newChanges[index] = {
              ...newChanges[index],
              changeDate: date ? date.format('YYYY-MM-DD') : '',
            };
            form.setFieldValue(['legalStatus', 'equityChanges'], newChanges);
            handleFormChange();
          }}
          disabled={readonly}
          placeholder="选择日期"
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: '变更类型',
      dataIndex: 'changeType',
      key: 'changeType',
      width: 120,
      render: (text: string, record: EquityChange, index: number) => (
        <Select
          value={text}
          onChange={value => {
            const changes =
              form.getFieldValue(['legalStatus', 'equityChanges']) || [];
            const newChanges = [...changes];
            newChanges[index] = { ...newChanges[index], changeType: value };
            form.setFieldValue(['legalStatus', 'equityChanges'], newChanges);
            handleFormChange();
          }}
          disabled={readonly}
          placeholder="选择类型"
          style={{ width: '100%' }}
        >
          <Option value="capital_increase">增资</Option>
          <Option value="capital_reduction">减资</Option>
          <Option value="equity_transfer">股权转让</Option>
          <Option value="merger">合并</Option>
          <Option value="split">分立</Option>
        </Select>
      ),
    },
    {
      title: '变更前比例(%)',
      dataIndex: 'beforeRatio',
      key: 'beforeRatio',
      width: 120,
      render: (value: number, record: EquityChange, index: number) => (
        <InputNumber
          value={value}
          onChange={val => {
            const changes =
              form.getFieldValue(['legalStatus', 'equityChanges']) || [];
            const newChanges = [...changes];
            newChanges[index] = { ...newChanges[index], beforeRatio: val || 0 };
            form.setFieldValue(['legalStatus', 'equityChanges'], newChanges);
            handleFormChange();
          }}
          disabled={readonly}
          placeholder="变更前"
          style={{ width: '100%' }}
          min={0}
          max={100}
          precision={2}
        />
      ),
    },
    {
      title: '变更后比例(%)',
      dataIndex: 'afterRatio',
      key: 'afterRatio',
      width: 120,
      render: (value: number, record: EquityChange, index: number) => (
        <InputNumber
          value={value}
          onChange={val => {
            const changes =
              form.getFieldValue(['legalStatus', 'equityChanges']) || [];
            const newChanges = [...changes];
            newChanges[index] = { ...newChanges[index], afterRatio: val || 0 };
            form.setFieldValue(['legalStatus', 'equityChanges'], newChanges);
            handleFormChange();
          }}
          disabled={readonly}
          placeholder="变更后"
          style={{ width: '100%' }}
          min={0}
          max={100}
          precision={2}
        />
      ),
    },
    {
      title: '变更原因',
      dataIndex: 'changeReason',
      key: 'changeReason',
      width: 200,
      render: (text: string, record: EquityChange, index: number) => (
        <TextArea
          value={text}
          onChange={e => {
            const changes =
              form.getFieldValue(['legalStatus', 'equityChanges']) || [];
            const newChanges = [...changes];
            newChanges[index] = {
              ...newChanges[index],
              changeReason: e.target.value,
            };
            form.setFieldValue(['legalStatus', 'equityChanges'], newChanges);
            handleFormChange();
          }}
          disabled={readonly}
          placeholder="变更原因"
          autoSize={{ minRows: 2, maxRows: 4 }}
        />
      ),
    },
    {
      title: '批准文件',
      dataIndex: 'approvalDocument',
      key: 'approvalDocument',
      width: 150,
      render: (text: string, record: EquityChange, index: number) => (
        <Input
          value={text}
          onChange={e => {
            const changes =
              form.getFieldValue(['legalStatus', 'equityChanges']) || [];
            const newChanges = [...changes];
            newChanges[index] = {
              ...newChanges[index],
              approvalDocument: e.target.value,
            };
            form.setFieldValue(['legalStatus', 'equityChanges'], newChanges);
            handleFormChange();
          }}
          disabled={readonly}
          placeholder="批准文件号"
        />
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 80,
      render: (text: any, record: EquityChange, index: number) => (
        <Button
          type="link"
          danger
          size="small"
          icon={<DeleteOutlined />}
          onClick={() => {
            const changes =
              form.getFieldValue(['legalStatus', 'equityChanges']) || [];
            const newChanges = [...changes];
            newChanges.splice(index, 1);
            form.setFieldValue(['legalStatus', 'equityChanges'], newChanges);
            handleFormChange();
          }}
          disabled={readonly}
        >
          删除
        </Button>
      ),
    },
  ];

  // 诉讼案件表格列定义
  const litigationColumns = [
    {
      title: '案件编号',
      dataIndex: 'caseNumber',
      key: 'caseNumber',
      width: 150,
      render: (text: string, record: LitigationCase, index: number) => (
        <Input
          value={text}
          onChange={e => {
            const cases =
              form.getFieldValue(['litigation', 'majorCases']) || [];
            const newCases = [...cases];
            newCases[index] = {
              ...newCases[index],
              caseNumber: e.target.value,
            };
            form.setFieldValue(['litigation', 'majorCases'], newCases);
            handleFormChange();
          }}
          disabled={readonly}
          placeholder="案件编号"
        />
      ),
    },
    {
      title: '案件类型',
      dataIndex: 'caseType',
      key: 'caseType',
      width: 120,
      render: (text: string, record: LitigationCase, index: number) => (
        <Select
          value={text}
          onChange={value => {
            const cases =
              form.getFieldValue(['litigation', 'majorCases']) || [];
            const newCases = [...cases];
            newCases[index] = { ...newCases[index], caseType: value };
            form.setFieldValue(['litigation', 'majorCases'], newCases);
            handleFormChange();
          }}
          disabled={readonly}
          placeholder="案件类型"
          style={{ width: '100%' }}
        >
          <Option value="civil">民事诉讼</Option>
          <Option value="commercial">商事诉讼</Option>
          <Option value="administrative">行政诉讼</Option>
          <Option value="arbitration">仲裁案件</Option>
          <Option value="criminal">刑事案件</Option>
          <Option value="labor">劳动争议</Option>
          <Option value="intellectual_property">知识产权纠纷</Option>
          <Option value="contract_dispute">合同纠纷</Option>
          <Option value="other">其他</Option>
        </Select>
      ),
    },
    {
      title: '原告',
      dataIndex: 'plaintiff',
      key: 'plaintiff',
      width: 150,
      render: (text: string, record: LitigationCase, index: number) => (
        <Input
          value={text}
          onChange={e => {
            const cases =
              form.getFieldValue(['litigation', 'majorCases']) || [];
            const newCases = [...cases];
            newCases[index] = { ...newCases[index], plaintiff: e.target.value };
            form.setFieldValue(['litigation', 'majorCases'], newCases);
            handleFormChange();
          }}
          disabled={readonly}
          placeholder="原告名称"
        />
      ),
    },
    {
      title: '被告',
      dataIndex: 'defendant',
      key: 'defendant',
      width: 150,
      render: (text: string, record: LitigationCase, index: number) => (
        <Input
          value={text}
          onChange={e => {
            const cases =
              form.getFieldValue(['litigation', 'majorCases']) || [];
            const newCases = [...cases];
            newCases[index] = { ...newCases[index], defendant: e.target.value };
            form.setFieldValue(['litigation', 'majorCases'], newCases);
            handleFormChange();
          }}
          disabled={readonly}
          placeholder="被告名称"
        />
      ),
    },
    {
      title: '涉案金额(万元)',
      dataIndex: 'involvedAmount',
      key: 'involvedAmount',
      width: 120,
      render: (value: number, record: LitigationCase, index: number) => (
        <InputNumber
          value={value}
          onChange={val => {
            const cases =
              form.getFieldValue(['litigation', 'majorCases']) || [];
            const newCases = [...cases];
            newCases[index] = { ...newCases[index], involvedAmount: val || 0 };
            form.setFieldValue(['litigation', 'majorCases'], newCases);
            handleFormChange();
          }}
          disabled={readonly}
          placeholder="涉案金额"
          style={{ width: '100%' }}
          min={0}
          precision={2}
        />
      ),
    },
    {
      title: '案件状态',
      dataIndex: 'caseStatus',
      key: 'caseStatus',
      width: 120,
      render: (text: string, record: LitigationCase, index: number) => (
        <Select
          value={text}
          onChange={value => {
            const cases =
              form.getFieldValue(['litigation', 'majorCases']) || [];
            const newCases = [...cases];
            newCases[index] = { ...newCases[index], caseStatus: value };
            form.setFieldValue(['litigation', 'majorCases'], newCases);
            handleFormChange();
          }}
          disabled={readonly}
          placeholder="案件状态"
          style={{ width: '100%' }}
        >
          <Option value="pending">待审理</Option>
          <Option value="in_progress">审理中</Option>
          <Option value="first_trial">一审完成</Option>
          <Option value="appeal">上诉中</Option>
          <Option value="final">终审完成</Option>
          <Option value="execution">执行中</Option>
          <Option value="settled">和解结案</Option>
          <Option value="closed">结案</Option>
        </Select>
      ),
    },
    {
      title: '立案日期',
      dataIndex: 'filingDate',
      key: 'filingDate',
      width: 120,
      render: (text: string, record: LitigationCase, index: number) => (
        <DatePicker
          value={text ? dayjs(text) : null}
          onChange={date => {
            const cases =
              form.getFieldValue(['litigation', 'majorCases']) || [];
            const newCases = [...cases];
            newCases[index] = {
              ...newCases[index],
              filingDate: date ? date.format('YYYY-MM-DD') : '',
            };
            form.setFieldValue(['litigation', 'majorCases'], newCases);
            handleFormChange();
          }}
          disabled={readonly}
          placeholder="立案日期"
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: '案件描述',
      dataIndex: 'caseDescription',
      key: 'caseDescription',
      width: 200,
      render: (text: string, record: LitigationCase, index: number) => (
        <TextArea
          value={text}
          onChange={e => {
            const cases =
              form.getFieldValue(['litigation', 'majorCases']) || [];
            const newCases = [...cases];
            newCases[index] = {
              ...newCases[index],
              caseDescription: e.target.value,
            };
            form.setFieldValue(['litigation', 'majorCases'], newCases);
            handleFormChange();
          }}
          disabled={readonly}
          placeholder="案件详细描述"
          autoSize={{ minRows: 2, maxRows: 4 }}
        />
      ),
    },
    {
      title: '潜在影响',
      dataIndex: 'potentialImpact',
      key: 'potentialImpact',
      width: 200,
      render: (text: string, record: LitigationCase, index: number) => (
        <TextArea
          value={text}
          onChange={e => {
            const cases =
              form.getFieldValue(['litigation', 'majorCases']) || [];
            const newCases = [...cases];
            newCases[index] = {
              ...newCases[index],
              potentialImpact: e.target.value,
            };
            form.setFieldValue(['litigation', 'majorCases'], newCases);
            handleFormChange();
          }}
          disabled={readonly}
          placeholder="对公司的潜在影响"
          autoSize={{ minRows: 2, maxRows: 4 }}
        />
      ),
    },
    {
      title: '风险等级',
      dataIndex: 'riskLevel',
      key: 'riskLevel',
      width: 100,
      render: (level: RiskLevel, record: LitigationCase, index: number) => (
        <Select
          value={level}
          onChange={value => {
            const cases =
              form.getFieldValue(['litigation', 'majorCases']) || [];
            const newCases = [...cases];
            newCases[index] = { ...newCases[index], riskLevel: value };
            form.setFieldValue(['litigation', 'majorCases'], newCases);
            handleFormChange();
          }}
          disabled={readonly}
          placeholder="风险等级"
          style={{ width: '100%' }}
        >
          <Option value="low">低风险</Option>
          <Option value="medium">中等风险</Option>
          <Option value="high">高风险</Option>
          <Option value="critical">极高风险</Option>
        </Select>
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 80,
      render: (text: any, record: LitigationCase, index: number) => (
        <Button
          type="link"
          danger
          size="small"
          icon={<DeleteOutlined />}
          onClick={() => {
            const cases =
              form.getFieldValue(['litigation', 'majorCases']) || [];
            const newCases = [...cases];
            newCases.splice(index, 1);
            form.setFieldValue(['litigation', 'majorCases'], newCases);
            handleFormChange();
          }}
          disabled={readonly}
        >
          删除
        </Button>
      ),
    },
  ];

  return (
    <div>
      {/* 自动保存指示器 */}
      <div className="mb-4 flex justify-between items-center">
        <h2 className="text-xl font-semibold">法律尽职调查</h2>
        <AutoSaveIndicator
          saving={saving}
          saveStatus={saveStatus}
          lastSavedText={lastSavedText}
        />
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        onValuesChange={handleFormChange}
        disabled={readonly}
      >
        {/* A. 公司法律地位核查 */}
        <FormSection
          title="公司法律地位核查"
          icon={<BankOutlined />}
          alert={{
            message: '请全面核查发行人的法律地位、设立合法性和合规状况',
            type: 'info',
          }}
        >
          <FormSubSection title="设立合法性">
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name={[
                    'legalStatus',
                    'establishmentLegality',
                    'registrationStatus',
                  ]}
                  label="工商注册状态"
                  rules={[DDValidationRules.required()]}
                >
                  <Select placeholder="请选择注册状态">
                    <Option value="valid">有效</Option>
                    <Option value="abnormal">异常</Option>
                    <Option value="cancelled">注销</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name={[
                    'legalStatus',
                    'establishmentLegality',
                    'registrationDate',
                  ]}
                  label="注册成立日期"
                  rules={[DDValidationRules.required()]}
                >
                  <DatePicker style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name={[
                    'legalStatus',
                    'establishmentLegality',
                    'businessLicense',
                  ]}
                  label="营业执照有效期"
                  rules={[DDValidationRules.required()]}
                >
                  <DatePicker style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>
          </FormSubSection>

          <FormSubSection title="股权结构变更">
            {/* 股权变更历史表格 */}
            <Form.Item
              name={['legalStatus', 'equityChanges']}
              initialValue={[]}
            >
              <Form.Item noStyle shouldUpdate>
                {() => {
                  const changes =
                    form.getFieldValue(['legalStatus', 'equityChanges']) || [];
                  return (
                    <>
                      <UnifiedDataTable
                        columns={equityChangeColumns}
                        dataSource={changes}
                        pagination={false}
                        size="small"
                        rowKey={(record, index) => `equity-change-${index}`}
                        locale={{
                          emptyText: '暂无股权变更记录，请点击下方按钮添加',
                        }}
                        scroll={{ x: 'max-content' }}
                      />
                      <Button
                        type="dashed"
                        onClick={() => {
                          const newChanges = [
                            ...changes,
                            {
                              changeDate: '',
                              changeType: '',
                              beforeRatio: 0,
                              afterRatio: 0,
                              changeReason: '',
                              approvalDocument: '',
                            },
                          ];
                          form.setFieldValue(
                            ['legalStatus', 'equityChanges'],
                            newChanges
                          );
                          handleFormChange();
                        }}
                        icon={<PlusOutlined />}
                        style={{ width: '100%', marginTop: 8 }}
                        disabled={readonly}
                      >
                        添加股权变更记录
                      </Button>
                    </>
                  );
                }}
              </Form.Item>
            </Form.Item>
          </FormSubSection>

          <FormSubSection title="合规性确认">
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name={['legalStatus', 'compliance', 'hasLegalViolations']}
                  label="是否存在违法违规"
                  rules={[DDValidationRules.required()]}
                >
                  <Radio.Group>
                    <Radio value={false}>否</Radio>
                    <Radio value={true}>是</Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
              <Col span={12}>
                <ConditionalField
                  dependsOn={[
                    'legalStatus',
                    'compliance',
                    'hasLegalViolations',
                  ]}
                  condition={value => value === true}
                >
                  <Form.Item
                    name={['legalStatus', 'compliance', 'violationDetails']}
                    label="违法违规详情"
                    rules={[DDValidationRules.required()]}
                  >
                    <TextArea rows={3} placeholder="请详细说明违法违规情况" />
                  </Form.Item>
                </ConditionalField>
              </Col>
            </Row>
          </FormSubSection>
        </FormSection>

        {/* B. 重大合同审查 */}
        <FormSection
          title="重大合同审查"
          icon={<FileTextOutlined />}
          alert={{
            message: '请核查所有重大合同的合法性、有效性和风险点',
            type: 'warning',
          }}
        >
          <FormSubSection title="重大合同清单">
            <Form.Item
              name={['majorContracts', 'contractList']}
              initialValue={[]}
            >
              <Form.Item noStyle shouldUpdate>
                {() => {
                  const contracts =
                    form.getFieldValue(['majorContracts', 'contractList']) ||
                    [];
                  return (
                    <>
                      <UnifiedDataTable
                        columns={majorContractColumns}
                        dataSource={contracts}
                        pagination={false}
                        size="small"
                        rowKey={(record, index) => `contract-${index}`}
                        locale={{
                          emptyText: '暂无重大合同记录，请点击下方按钮添加',
                        }}
                        scroll={{ x: 'max-content' }}
                      />
                      <Button
                        type="dashed"
                        onClick={() => {
                          const newContracts = [
                            ...contracts,
                            {
                              contractName: '',
                              contractType: '',
                              counterparty: '',
                              contractAmount: 0,
                              signDate: '',
                              effectiveDate: '',
                              expiryDate: '',
                              keyTerms: '',
                              riskAssessment: '',
                            },
                          ];
                          form.setFieldValue(
                            ['majorContracts', 'contractList'],
                            newContracts
                          );
                          handleFormChange();
                        }}
                        icon={<PlusOutlined />}
                        style={{ width: '100%', marginTop: 8 }}
                        disabled={readonly}
                      >
                        添加重大合同
                      </Button>
                    </>
                  );
                }}
              </Form.Item>
            </Form.Item>
          </FormSubSection>
        </FormSection>

        {/* C. 诉讼仲裁调查 */}
        <FormSection
          title="诉讼仲裁调查"
          icon={<ExclamationCircleOutlined />}
          alert={{
            message: '请全面调查发行人涉及的诉讼、仲裁案件及潜在风险',
            type: 'error',
          }}
        >
          <FormSubSection title="诉讼情况统计">
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name={['litigation', 'summary', 'ongoingCases']}
                  label="进行中案件数量"
                  rules={[DDValidationRules.required()]}
                >
                  <InputNumber
                    min={0}
                    placeholder="请输入案件数量"
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name={['litigation', 'summary', 'completedCases']}
                  label="已结案件数量"
                  rules={[DDValidationRules.required()]}
                >
                  <InputNumber
                    min={0}
                    placeholder="请输入案件数量"
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name={['litigation', 'summary', 'totalInvolvedAmount']}
                  label="涉案总金额(万元)"
                  rules={[
                    DDValidationRules.required(),
                    DDValidationRules.litigationAmount(),
                  ]}
                >
                  <InputNumber
                    min={0}
                    precision={2}
                    placeholder="请输入涉案金额"
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
            </Row>
          </FormSubSection>

          <FormSubSection title="重大诉讼详情">
            <Form.Item name={['litigation', 'majorCases']} initialValue={[]}>
              <Form.Item noStyle shouldUpdate>
                {() => {
                  const cases =
                    form.getFieldValue(['litigation', 'majorCases']) || [];
                  return (
                    <>
                      <UnifiedDataTable
                        columns={litigationColumns}
                        dataSource={cases}
                        pagination={false}
                        size="small"
                        rowKey={(record, index) => `litigation-${index}`}
                        locale={{
                          emptyText: '暂无重大诉讼案件记录，请点击下方按钮添加',
                        }}
                        scroll={{ x: 'max-content' }}
                      />
                      <Button
                        type="dashed"
                        onClick={() => {
                          const newCases = [
                            ...cases,
                            {
                              caseNumber: '',
                              caseType: '',
                              plaintiff: '',
                              defendant: '',
                              involvedAmount: 0,
                              caseStatus: '',
                              filingDate: '',
                              caseDescription: '',
                              potentialImpact: '',
                              riskLevel: 'low',
                            },
                          ];
                          form.setFieldValue(
                            ['litigation', 'majorCases'],
                            newCases
                          );
                          handleFormChange();
                        }}
                        icon={<PlusOutlined />}
                        style={{ width: '100%', marginTop: 8 }}
                        disabled={readonly}
                      >
                        添加诉讼案件
                      </Button>
                    </>
                  );
                }}
              </Form.Item>
            </Form.Item>
          </FormSubSection>
        </FormSection>

        {/* D. 知识产权核查 */}
        <FormSection
          title="知识产权核查"
          icon={<CopyrightOutlined />}
          alert={{
            message: '请全面核查发行人的知识产权状况，包括专利、商标、著作权等',
            type: 'info',
          }}
        >
          <FormSubSection title="知识产权统计">
            <Row gutter={16}>
              <Col span={6}>
                <Form.Item
                  name={['intellectualProperty', 'summary', 'patentCount']}
                  label="专利数量"
                  rules={[DDValidationRules.required()]}
                >
                  <InputNumber
                    min={0}
                    placeholder="专利数量"
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  name={['intellectualProperty', 'summary', 'trademarkCount']}
                  label="商标数量"
                  rules={[DDValidationRules.required()]}
                >
                  <InputNumber
                    min={0}
                    placeholder="商标数量"
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  name={['intellectualProperty', 'summary', 'copyrightCount']}
                  label="著作权数量"
                  rules={[DDValidationRules.required()]}
                >
                  <InputNumber
                    min={0}
                    placeholder="著作权数量"
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  name={['intellectualProperty', 'summary', 'tradeSecretCount']}
                  label="商业秘密数量"
                  rules={[DDValidationRules.required()]}
                >
                  <InputNumber
                    min={0}
                    placeholder="商业秘密数量"
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
            </Row>
          </FormSubSection>

          <FormSubSection title="知识产权有效性评估">
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name={['intellectualProperty', 'validity', 'validPatents']}
                  label="有效专利数量"
                  rules={[DDValidationRules.required()]}
                >
                  <InputNumber
                    min={0}
                    placeholder="有效专利数量"
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name={['intellectualProperty', 'validity', 'expiringSoon']}
                  label="即将到期数量"
                  rules={[DDValidationRules.required()]}
                >
                  <InputNumber
                    min={0}
                    placeholder="2年内到期数量"
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name={['intellectualProperty', 'validity', 'inDispute']}
                  label="争议中数量"
                  rules={[DDValidationRules.required()]}
                >
                  <InputNumber
                    min={0}
                    placeholder="争议中数量"
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
            </Row>
          </FormSubSection>

          <FormSubSection title="核心知识产权风险评估">
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name={[
                    'intellectualProperty',
                    'risks',
                    'hasInfringementRisk',
                  ]}
                  label="是否存在侵权风险"
                  rules={[DDValidationRules.required()]}
                >
                  <Radio.Group>
                    <Radio value={false}>否</Radio>
                    <Radio value={true}>是</Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
              <Col span={12}>
                <ConditionalField
                  dependsOn={[
                    'intellectualProperty',
                    'risks',
                    'hasInfringementRisk',
                  ]}
                  condition={value => value === true}
                >
                  <Form.Item
                    name={[
                      'intellectualProperty',
                      'risks',
                      'infringementDetails',
                    ]}
                    label="侵权风险详情"
                    rules={[DDValidationRules.required()]}
                  >
                    <TextArea rows={3} placeholder="请详细说明侵权风险情况" />
                  </Form.Item>
                </ConditionalField>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name={['intellectualProperty', 'risks', 'hasLicenseRisk']}
                  label="是否存在许可风险"
                  rules={[DDValidationRules.required()]}
                >
                  <Radio.Group>
                    <Radio value={false}>否</Radio>
                    <Radio value={true}>是</Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
              <Col span={12}>
                <ConditionalField
                  dependsOn={[
                    'intellectualProperty',
                    'risks',
                    'hasLicenseRisk',
                  ]}
                  condition={value => value === true}
                >
                  <Form.Item
                    name={[
                      'intellectualProperty',
                      'risks',
                      'licenseRiskDetails',
                    ]}
                    label="许可风险详情"
                    rules={[DDValidationRules.required()]}
                  >
                    <TextArea rows={3} placeholder="请详细说明许可风险情况" />
                  </Form.Item>
                </ConditionalField>
              </Col>
            </Row>
          </FormSubSection>

          <FormSubSection title="知识产权价值评估">
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name={['intellectualProperty', 'valuation', 'totalValue']}
                  label="知识产权总价值(万元)"
                  rules={[
                    DDValidationRules.required(),
                    DDValidationRules.ipValue(),
                  ]}
                >
                  <InputNumber
                    min={0}
                    precision={2}
                    placeholder="知识产权总价值"
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name={[
                    'intellectualProperty',
                    'valuation',
                    'valuationMethod',
                  ]}
                  label="评估方法"
                  rules={[DDValidationRules.required()]}
                >
                  <Select placeholder="选择评估方法">
                    <Option value="cost_approach">成本法</Option>
                    <Option value="market_approach">市场法</Option>
                    <Option value="income_approach">收益法</Option>
                    <Option value="mixed_approach">混合法</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name={['intellectualProperty', 'valuation', 'valuationDate']}
                  label="评估基准日"
                  rules={[DDValidationRules.required()]}
                >
                  <DatePicker style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name={['intellectualProperty', 'valuation', 'valuationReport']}
              label="价值评估说明"
            >
              <TextArea
                rows={4}
                placeholder="请说明知识产权价值评估的依据和方法"
              />
            </Form.Item>
          </FormSubSection>
        </FormSection>

        {/* E. 法律风险评估 */}
        <FormSection
          title="法律风险评估"
          icon={<AlertOutlined />}
          alert={{
            message: '请基于尽职调查结果，全面评估法律风险并提出专业意见',
            type: 'info',
          }}
        >
          <FormSubSection title="风险评估矩阵">
            <Form.Item
              name={['legalRiskAssessment', 'riskMatrix']}
              initialValue={[]}
            >
              <RiskMatrix
                riskCategories={[
                  { value: 'corporate_legal', label: '公司法律风险' },
                  { value: 'contract_risk', label: '合同风险' },
                  { value: 'litigation_risk', label: '诉讼风险' },
                  { value: 'ip_risk', label: '知识产权风险' },
                  { value: 'compliance_risk', label: '合规风险' },
                  { value: 'regulatory_risk', label: '监管风险' },
                  { value: 'operational_legal_risk', label: '运营法律风险' },
                ]}
                readonly={readonly}
                emptyText="暂无法律风险评估，请点击下方按钮添加"
                addButtonText="添加法律风险"
                showResponsibleParty={true}
                showTimeline={true}
                onChange={value => {
                  form.setFieldValue(
                    ['legalRiskAssessment', 'riskMatrix'],
                    value
                  );
                  handleFormChange();
                }}
              />
            </Form.Item>
          </FormSubSection>

          <FormSubSection title="综合法律意见">
            <Form.Item
              name={['legalRiskAssessment', 'overallOpinion', 'conclusion']}
              label="法律尽调结论"
              rules={[DDValidationRules.required()]}
            >
              <Select placeholder="请选择法律尽调结论">
                <Option value="compliant">合规无重大风险</Option>
                <Option value="minor_issues">存在轻微问题</Option>
                <Option value="moderate_risks">存在中等风险</Option>
                <Option value="major_concerns">存在重大法律风险</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name={[
                'legalRiskAssessment',
                'overallOpinion',
                'detailedOpinion',
              ]}
              label="详细法律意见"
              rules={[
                DDValidationRules.required(),
                DDValidationRules.legalOpinion(100),
              ]}
            >
              <TextArea
                rows={6}
                placeholder="请详细阐述法律尽职调查的结论和建议（至少100字）"
              />
            </Form.Item>

            <Form.Item
              name={[
                'legalRiskAssessment',
                'overallOpinion',
                'recommendations',
              ]}
              label="改进建议"
            >
              <TextArea rows={4} placeholder="请提出具体的法律合规改进建议" />
            </Form.Item>
          </FormSubSection>
        </FormSection>

        {/* 文档上传区域 */}
        <FormSection
          title="相关文档"
          icon={<FileTextOutlined />}
          alert={{
            message:
              '发行人和中介机构都可以上传相关文档。发行人提供基础材料，中介机构补充尽职调查报告和专业意见。',
            type: 'info',
          }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <DocumentUpload
                projectId={projectId}
                stepId="legal_due_diligence_corporate"
                title="公司法律文件"
                description="营业执照、公司章程、股东决议、董事会决议等"
                maxFileSize={20}
                maxFileCount={15}
                allowedFileTypes={['.pdf', '.doc', '.docx', '.jpg', '.png']}
                readonly={readonly}
              />
            </Col>
            <Col span={12}>
              <DocumentUpload
                projectId={projectId}
                stepId="legal_due_diligence_contracts"
                title="重大合同文件"
                description="重大销售合同、采购合同、服务合同、担保合同等"
                maxFileSize={20}
                maxFileCount={20}
                allowedFileTypes={['.pdf', '.doc', '.docx']}
                readonly={readonly}
              />
            </Col>
          </Row>

          <Row gutter={16} style={{ marginTop: EMERALD_THEME.spacing.md }}>
            <Col span={12}>
              <DocumentUpload
                projectId={projectId}
                stepId="legal_due_diligence_litigation"
                title="诉讼仲裁文件"
                description="起诉书、判决书、仲裁裁决书、和解协议等"
                maxFileSize={20}
                maxFileCount={10}
                allowedFileTypes={['.pdf', '.doc', '.docx', '.jpg', '.png']}
                readonly={readonly}
              />
            </Col>
            <Col span={12}>
              <DocumentUpload
                projectId={projectId}
                stepId="legal_due_diligence_ip"
                title="知识产权文件"
                description="专利证书、商标注册证、著作权登记证等"
                maxFileSize={20}
                maxFileCount={15}
                allowedFileTypes={['.pdf', '.doc', '.docx', '.jpg', '.png']}
                readonly={readonly}
              />
            </Col>
          </Row>
        </FormSection>

        {/* 禁止在组件内部添加操作按钮！ */}
        {/* 所有操作必须通过统一底部操作栏处理 */}
      </Form>
    </div>
  );
};

export default LegalDueDiligence;
